import * as React from "react";
import {
  TopToolbar,
  useDataProvider,
  useListContext,
  useResourceContext,
} from "react-admin";
import DownloadIcon from "@mui/icons-material/Download";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Button from "@mui/material/Button";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import { useMutation } from "react-query";
import PdfMyIcon from "../myIcons/PdfMyIcon";
import ExcelMyIcon from "../myIcons/ExcelMyIcon";

const DownloadAction = ({ field, order, downloadMenus }) => {
  const { filterValues, sort } = useListContext();
  const resource = useResourceContext();
  const dataProvider = useDataProvider();
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);

  const resolvedDownloadMenus = React.useMemo(() => {
    if (Array.isArray(downloadMenus) && downloadMenus.length) {
      return downloadMenus;
    }

    return [
      {
        id: "excel",
        label: "Excel",
        path: "/excel",
        icon: <ExcelMyIcon />,
      },
      {
        id: "pdf",
        label: "Pdf",
        path: "/pdf",
        icon: <PdfMyIcon />,
      },
    ];
  }, [downloadMenus]);

  const menuWidth = React.useMemo(() => {
    const longestLabelLength = resolvedDownloadMenus.reduce(
      (maxLength, menu) => Math.max(maxLength, String(menu?.label || "").length),
      0
    );

    if (longestLabelLength >= 16) {
      return 220;
    }
    if (longestLabelLength >= 12) {
      return 180;
    }
    return 120;
  }, [resolvedDownloadMenus]);

  const { data, mutate } = useMutation((menuConfig) => {
    const exportPath = menuConfig?.path || "/excel";
    const mergedFilterValues = {
      ...(filterValues || {}),
      ...(menuConfig?.filterPatch || {}),
    };

    let init = resource + exportPath;

    let questionOrAmpersand = "&";

    if (Object.keys(mergedFilterValues).length) {
      init += "?filter=" + encodeURIComponent(JSON.stringify(mergedFilterValues));
    } else {
      questionOrAmpersand = "?";
    }

    init +=
      questionOrAmpersand +
      "sort=" +
      encodeURIComponent(
        JSON.stringify([
          (field ? field : sort.field) + " " + (order ? order : sort.order),
        ])
      );
    return dataProvider.getListNoParams(init);
  });

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleDownload = (menuConfig) => {
    mutate(menuConfig);
  };

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === "Escape") {
      setOpen(false);
    }
  }

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  React.useEffect(() => {
    if (data && data.link) {
      setOpen(false);
      const newlink = document.createElement("a");
      newlink.setAttribute("href", data.link);
      newlink.setAttribute("target", "_blank");
      document.body.appendChild(newlink);

      var clickEvent = new MouseEvent("click", {
        view: window,
        bubbles: true,
        cancelable: false,
      });
      newlink.dispatchEvent(clickEvent);
      newlink.remove();
    }
  }, [data]);

  return (
    <TopToolbar
      sx={{
        minHeight: "54px !important",
        // mb: 1,
        "& .MuiButtonBase-root": {
          border: "0.1rem solid",
        },
      }}
    >
      <Button
        ref={anchorRef}
        label="Download"
        sx={{ width: "fit-content", lineHeight: "1.5" }}
        id="composition-button"
        aria-controls={open ? "composition-menu" : undefined}
        aria-expanded={open ? "true" : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        startIcon={<DownloadIcon />}
        size="small"
      >
        Export
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-end"
        transition
        sx={{ zIndex: 1300, width: menuWidth, maxWidth: "100%" }}
        modifiers={[
          {
            name: "preventOverflow",
            options: {
              boundary: "viewport",
            },
          },
          {
            name: "flip",
            options: {
              fallbackPlacements: ["bottom-start", "top-end", "top-start"],
            },
          },
        ]}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom-end" ? "right top" : "right bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="composition-menu"
                  aria-labelledby="composition-button"
                  onKeyDown={handleListKeyDown}
                >
                  {resolvedDownloadMenus.map((menuConfig) => {
                    const path = String(menuConfig?.path || "").toLowerCase();
                    let icon = menuConfig?.icon || null;
                    if (!icon && path.includes("excel")) {
                      icon = <ExcelMyIcon />;
                    } else if (!icon && path.includes("pdf")) {
                      icon = <PdfMyIcon />;
                    }

                    return (
                      <MenuItem
                        key={menuConfig?.id || menuConfig?.label}
                        onClick={() => handleDownload(menuConfig)}
                        sx={{ border: "none !important" }}
                      >
                        <ListItemIcon>{icon}</ListItemIcon>
                        <ListItemText>{menuConfig?.label || "Export"}</ListItemText>
                      </MenuItem>
                    );
                  })}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </TopToolbar>
  );
};

export default DownloadAction;
